import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { authCallback, getMe, logout as apiLogout, awardXp as apiAwardXp } from "@/lib/api";

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const earnedNoticeShown = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me || null);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Skip /me during the OAuth round-trip — AuthCallback handles it.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const handleSessionId = useCallback(async (sessionId) => {
    try {
      const u = await authCallback(sessionId);
      setUser(u);
      return u;
    } catch (e) {
      toast.error("Sign in failed. Please try again.");
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  }, []);

  const award = useCallback(async (amount, source) => {
    if (!user) {
      if (!earnedNoticeShown.current) {
        earnedNoticeShown.current = true;
        toast.message("Sign in with Google to earn XP for correct answers.");
      }
      return null;
    }
    try {
      const res = await apiAwardXp({ amount, source });
      setUser((prev) => (prev ? { ...prev, xp: res.user_xp } : prev));
      return res;
    } catch {
      return null;
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, handleSessionId, award }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
